import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, IntegrationPending, Loading } from "@/components/common/Common";
import { ChannelAssistant } from "@/components/channels/ChannelAssistant";
import { useScope } from "@/context/ScopeContext";
import { api, errMsg } from "@/lib/api";
import { brl, num, pct } from "@/lib/format";

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function DemandGenForm({ accountId, onDone }) {
  const [f, setF] = useState({ name: "", daily_budget: 20, location: "Brasil", youtube_video_id: "", final_url: "", logo_url: "", business_name: "", headlines: "", long_headlines: "", descriptions: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!window.confirm(`Criar a campanha "${f.name}" no Google Ads? Ela será criada PAUSADA e só começa a veicular quando você ativar.`)) return;
    setBusy(true);
    try {
      const vid = (f.youtube_video_id.match(/(?:v=|youtu\.be\/|shorts\/)([A-Za-z0-9_-]{11})/) || [])[1] || f.youtube_video_id.trim();
      await api.post("/channels/youtube/demand-gen", { ...f, account_id: accountId, youtube_video_id: vid, daily_budget: Number(f.daily_budget), headlines: lines(f.headlines), long_headlines: lines(f.long_headlines), descriptions: lines(f.descriptions), confirm: true });
      toast.success("Campanha criada no Google Ads (pausada)"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const F = (k, l, p, t) => <div><Label>{l}</Label>{t ? <Textarea rows={3} placeholder={p} value={f[k]} onChange={set(k)} data-testid={`yt-${k}`} /> : <Input placeholder={p} value={f[k]} onChange={set(k)} data-testid={`yt-${k}`} />}</div>;
  return (
    <section className="surface p-4 space-y-3" data-testid="yt-demand-gen-form">
      <p className="font-semibold">Nova campanha de vídeo (Demand Gen: YouTube In-stream, In-feed e Shorts)</p>
      <div className="grid md:grid-cols-3 gap-3">{F("name", "Nome da campanha", "Ex.: Vídeo Black Friday")}{F("daily_budget", "Orçamento diário (R$)", "20")}{F("location", "Localização", "Brasil, São Paulo...")}
        {F("youtube_video_id", "Link ou ID do vídeo no YouTube", "https://youtu.be/...")}{F("final_url", "URL final", "https://seusite.com.br")}{F("logo_url", "Link da imagem do logo (quadrado)", "https://.../logo.png")}
        {F("business_name", "Nome da empresa (até 25)", "Cem Zebra")}</div>
      <div className="grid md:grid-cols-3 gap-3">{F("headlines", "Títulos (1 por linha, até 40)", "", true)}{F("long_headlines", "Títulos longos (1 por linha, até 90)", "", true)}{F("descriptions", "Descrições (1 por linha, até 90)", "", true)}</div>
      <Button onClick={submit} disabled={busy || !f.name} className="rounded-full" data-testid="yt-create-button">{busy ? "Criando..." : "Revisar e criar pausada"}</Button>
    </section>
  );
}

export default function YouTubeChannel() {
  const { accountId } = useScope();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    if (!accountId) return;
    setErr("");
    try { setRows((await api.get("/channels/youtube/campaigns", { params: { account_id: accountId } })).data); } catch (e) { setErr(errMsg(e)); setRows([]); }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="youtube-channel">
      <PageHeader eyebrow="Canais de Anúncios" title="YouTube Ads" desc="Campanhas de vídeo pela API oficial do Google Ads, usando a conexão que você já tem." />
      {!accountId ? <IntegrationPending desc="Conecte e selecione uma conta Google Ads para ver e criar campanhas de vídeo." /> : (
        <>
          <section className="surface p-4" data-testid="yt-campaigns">
            <p className="font-semibold mb-2">Campanhas de vídeo e Demand Gen (últimos 30 dias)</p>
            {err && <p className="text-sm text-[#EA4335]">{err}</p>}
            {rows === null ? <Loading /> : rows.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma campanha de vídeo nesta conta.</p> : (
              <div className="overflow-x-auto"><Table><TableHeader><TableRow>{["Campanha", "Tipo", "Status", "Orçamento", "Impressões", "Visualizações", "Cliques", "CTR", "Custo", "Conversões"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>{rows.map((r) => <TableRow key={r.id}><TableCell>{r.name}</TableCell><TableCell>{r.type === "VIDEO" ? "Vídeo" : "Demand Gen"}</TableCell><TableCell>{r.status}</TableCell><TableCell>{brl(r.budget)}</TableCell><TableCell>{num(r.impressions)}</TableCell><TableCell>{num(r.views)}</TableCell><TableCell>{num(r.clicks)}</TableCell><TableCell>{pct(r.ctr)}</TableCell><TableCell>{brl(r.cost)}</TableCell><TableCell>{num(r.conversions, 1)}</TableCell></TableRow>)}</TableBody></Table></div>
            )}
          </section>
          <DemandGenForm accountId={accountId} onDone={load} />
        </>
      )}
      <ChannelAssistant channel="youtube" label="YouTube" />
    </div>
  );
}
